import React, { useState, useEffect } from "react";
import axios from "axios";
import { apiUrl } from "../../constants";
import SearchResult from "../searchResult/SearchResult";
import SearchResult2 from "../searchResult2/SearchResult2";
import FunctionalButton from "../button/FunctionalButton";
import SelectionToggle from "../selectionToggle/SelectionToggle";
import styles from "./Search.module.css";
import { getSpecsheetPublisher } from "../../helpers/specsheetNavigation";

function Search({ showNotActiveToggle }) {
  const [result, setResult] = useState([]);
  const [result2, setResult2] = useState([]);
  const [filterResult, setFilterResult] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [noResultMessage, toggleNoResultMessage] = useState(false);
  const [country, setCountry] = useState("Netherlands");
  const [active, setActive] = useState("active");
  const [publisherResults, setPublisherResults] = useState([]);
  const [standardResults, setStandardResults] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  //Active options for the active toggle
  const activeOptions = ["Active", "Not active"];

  //Country options for the country toggle are fetched from the database and sorted
  useEffect(() => {
    async function getCountryData() {
      const jwtToken = localStorage.getItem("token");
      try {
        const { data } = await axios.get(`${apiUrl}/api/formsettings/countries`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        });

        const countryOptions = [];

        data.forEach((item) => {
          countryOptions.push(item.country);
        });

        setCountryOptions(countryOptions.sort());
      } catch (e) {
        console.error(e);
      }
    }
    getCountryData();
  }, []);

  // Here we get the active data when loading the page and update it when the country is changed.
  useEffect(() => {
    getInitialData();
    getInitialData2();

    if (country) {
      getInitialData();
      getInitialData2();
      setSearchInput("");
      toggleNoResultMessage(false);
    }
  // The loader reads the current filter state when this effect runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  // Here we get the active data when loading the page and update it when the active drop down is changed.
  useEffect(() => {
    getInitialData();
    getInitialData2();

    if (country) {
      getInitialData();
      getInitialData2();
      setSearchInput("");
      toggleNoResultMessage(false);
    }
  // The loader reads the current filter state when this effect runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Here we get the data form the database
  async function getInitialData() {
    const jwtToken = localStorage.getItem("token");
    try {
      const { data } = await axios.get(
        `${apiUrl}/api/specsheets/initaldata/${country}/${active}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      setResult(data.map((item) => ({ ...item, dataSource: "specsheets" })));
    } catch (e) {
      console.error(e);
    }
  }

  // Here we get the data form the database
  async function getInitialData2() {
    const jwtToken = localStorage.getItem("token");
    try {
      const { data } = await axios.get(
        `${apiUrl}/api/specsheets2/initaldata/${country}/${active}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      setResult2(data.map((item) => ({ ...item, dataSource: "specsheets2" })));
    } catch (e) {
      console.error(e);
    }
  }

  // Sort and group records from both specsheet collections together.
  useEffect(() => {
    sortResults([...result, ...result2]);
  // Sorting is intentionally triggered only when either source result changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, result2]);

  // Here we activate the sortResult function when filterResult is updated.
  useEffect(() => {
    sortResults(filterResult);
  // Sorting is intentionally triggered only when the filtered result changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterResult]);

  // Here we sort and split the results before displaying on the page.
  function isStandardSpecsheet(item) {
    if (item.dataSource === "specsheets2") {
      return item.specsheet.category.toLowerCase() === "standard";
    }
    return item.specsheet.templateName.split("-")[0].trim().toLowerCase() === "standard";
  }

  function getPublisherLabel(item) {
    return item.dataSource === "specsheets2"
      ? getSpecsheetPublisher(item.specsheet)
      : item.specsheet.templateName.split("-")[0].trim();
  }

  function sortResults(resultsToSort) {
    const publisherSpecsheets = resultsToSort
      .filter((item) => !isStandardSpecsheet(item))
      .sort((a, b) =>
        getPublisherLabel(a).localeCompare(getPublisherLabel(b), undefined, {
          sensitivity: "base",
        }) || a.specsheet.templateName.localeCompare(b.specsheet.templateName, undefined, {
          sensitivity: "base",
        })
      );
    const standardSpecsheets = resultsToSort
      .filter(isStandardSpecsheet)
      .sort((a, b) =>
        (a.specsheet.listSubheader || "").localeCompare(
          b.specsheet.listSubheader || "",
          undefined,
          { sensitivity: "base" }
        ) ||
        getPublisherLabel(a).localeCompare(getPublisherLabel(b), undefined, {
          sensitivity: "base",
        }) ||
        a.specsheet.templateName.localeCompare(b.specsheet.templateName, undefined, {
          sensitivity: "base",
        })
      );

    setPublisherResults(publisherSpecsheets);
    setStandardResults(standardSpecsheets);
  }

  // After searching we can get the original list of specsheets again
  function clearSearch() {
    getInitialData();
    getInitialData2();
    setSearchInput("");
    toggleNoResultMessage(false);
  }

  // Here we filter the list with results on every keypress
  function filterResultsList(value) {
    toggleNoResultMessage(false);
    const newResult = [];
    const idArray = [];
    // Here we check the specsheetname
    [...result, ...result2].forEach((specsheetItem) => {
      const matchesTemplate = specsheetItem.specsheet.templateName
        .toLowerCase()
        .includes(value);
      const matchesKeyword = (specsheetItem.specsheet.keywords || []).some((keyword) =>
        keyword.value.toLowerCase().includes(value)
      );
      const uniqueId = `${specsheetItem.dataSource}-${specsheetItem._id}`;

      if ((matchesTemplate || matchesKeyword) && !idArray.includes(uniqueId)) {
        newResult.push(specsheetItem);
        idArray.push(uniqueId);
      }
    });
    if (newResult.length === 0) {
      toggleNoResultMessage(true);
    }
    setFilterResult(newResult);
  }

  // Here we check if we should display the subheader
  function publisherSubheader(item, index, specsheets) {
    const publisher = getPublisherLabel(item);
    if (index === 0 || publisher !== getPublisherLabel(specsheets[index - 1])) {
      return <h4 className={styles["publisher-subheader"]}>{publisher}</h4>;
    }
  }

  function standardSubheader(item, index, specsheets) {
    const listSubheader = item.specsheet.listSubheader;
    if (index === 0 || listSubheader !== specsheets[index - 1].specsheet.listSubheader) {
      return <h4 className={styles["publisher-subheader"]}>{listSubheader}</h4>;
    }
  }

  return (
    <div className={styles["container-search"]}>
      <div className={styles["container-settings"]}>
        {countryOptions && (
          <SelectionToggle
            set={setCountry}
            selected={country}
            setSelected={setCountry}
            options={countryOptions}
          />
        )}
        {showNotActiveToggle && (
          <SelectionToggle
            set={setActive}
            selected={active}
            setSelected={setActive}
            options={activeOptions}
          />
        )}
      </div>
      <div className={styles.container}>
        <div className={styles["search-container"]}>
          <div className={styles["search-form-container"]}>
            <div className={styles["search-form"]}>
              <p>Search for a specsheet</p>
              <div className={styles["inputfield-wrapper"]}>
                <input
                  value={searchInput}
                  className={styles["search-input-field"]}
                  placeholder="keyword..."
                  onChange={(e) => {
                    setSearchInput(e.target.value.toLowerCase());
                    filterResultsList(e.target.value.toLowerCase());
                  }}
                />
              </div>
            </div>
            {noResultMessage && (
              <span className={styles.error}>No results please try again.</span>
            )}
            <div className={styles["clear-button-wrapper"]}>
              <FunctionalButton type="button" handleClick={clearSearch}>
                Clear Search
              </FunctionalButton>
            </div>
          </div>
        </div>

        <>
          <div className={styles["result-container"]}>
            <h2>Publisher</h2>

            {publisherResults?.map((sheet, index) => {
              const { _id, specsheet } = sheet;

              return (
                <div key={`${sheet.dataSource}-${_id}`}>
                  {publisherSubheader(sheet, index, publisherResults)}
                  {sheet.dataSource === "specsheets2" ? (
                    <SearchResult2
                      key={_id}
                      id={_id}
                      specsheet={specsheet}
                      active={specsheet.active}
                    />
                  ) : (
                    <SearchResult
                      key={_id}
                      id={_id}
                      dsVersion={specsheet.dsVersion}
                      titleTemplate={specsheet.templateName}
                      active={specsheet.active}
                    />
                  )}
                </div>
              );
            })}

            <br />

            <h2>Standard</h2>

            {standardResults?.map((sheet, index) => {
              const { _id, specsheet } = sheet;

              return (
                <div key={`${sheet.dataSource}-${_id}`}>
                  {standardSubheader(sheet, index, standardResults)}
                  {sheet.dataSource === "specsheets2" ? (
                    <SearchResult2
                      key={_id}
                      id={_id}
                      specsheet={specsheet}
                      active={specsheet.active}
                    />
                  ) : (
                    <SearchResult
                      key={_id}
                      id={_id}
                      dsVersion={specsheet.dsVersion}
                      titleTemplate={specsheet.templateName}
                      active={specsheet.active}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      </div>
    </div>
  );
}

export default Search;
